import {action, computed, makeObservable, observable, runInAction} from "mobx";

import {HttpError} from "../../../api/http-error";
import {CategoryGroupInput} from "../../../api/values/category-group-create-input";
import {CategoryGroupDetailOutput} from "../../../api/values/category-group-detail-output";
import {CategoryInputOutput} from "../../../api/values/category-input-output";
import {Stores} from "../../../stores";
import {Permission} from "../../../stores/authentication-store";
import {CategoryGroupFormStore, TableRow} from "../../../stores/cagegory-group-form-store";
import {buildDeleteMessage} from "../../../utils/delete-message";
import {PageStoreConstructorOptions} from "../../../utils/page-store";
import {deleteDialogStore} from "../../organisms/delete-dialog/delete-dialog-store";

export class CategoryGroupEditPageStore extends CategoryGroupFormStore {
    @observable public mediaId: string = "";
    @observable public categoryGroupId: string = "";
    @observable public categoryGroup: CategoryGroupDetailOutput = new CategoryGroupDetailOutput();
    /** ■ 追加 ■ ロード直後のスナップショットを保持する */
    @observable private _originalCategoryGroup: {label: string; slug: string} = {
        label: "",
        slug: "",
    };
    @observable private _originalTableRows: TableRow[] = [];

    private adminApi;
    private pageRouter;
    private stores: Stores;
    constructor(options: PageStoreConstructorOptions) {
        super();
        makeObservable(this);
        this.adminApi = options.adminApi;
        this.pageRouter = options.pageRouter;
        this.stores = options.stores;
    }

    /** ページ初期化時に呼び出す */
    @action.bound
    public async initialize({query}: {query: {mediaId: string; categoryGroupId: string}}) {
        this.stores.authenticationStore.checkAnyPermissionExists(
            Permission.MANAGE_SYSTEM,
            Permission.MANAGE_MEDIA,
        );
        this.setMediaId(query.mediaId || "");
        this.setCategoryGroupId(query.categoryGroupId || "");
        await this.load();
    }

    @action
    public setMediaId(mediaId: string) {
        this.mediaId = mediaId;
    }
    @action
    public setCategoryGroupId(categoryGroupId: string) {
        this.categoryGroupId = categoryGroupId;
    }

    /** API から詳細を取得してフォームに反映 */
    @action.bound
    public async load() {
        const res = await this.adminApi.getCategoryGroupDetail(
            {mediaId: this.mediaId},
            {categoryGroupId: this.categoryGroupId},
        );

        runInAction(() => {
            this.categoryGroup = res;
            // フォーム共通ロジック側にセット
            this.groupLabel = res.label;
            this.groupSlug = res.slug;
            this.tableRows = res.categories.map((c) => ({
                categoryId: c.categoryId,
                label: c.label,
                slug: c.slug,
                labelError: "",
                slugError: "",
                count: c.count ?? 0,
            }));
            // ■■ ここで “ロード直後のスナップショット” をコピーしておく ■■
            this._originalCategoryGroup = {
                label: res.label,
                slug: res.slug,
            };
            // テーブル行のスナップショットは、必要なフィールドのみ（ID・label・slug・count）をコピー
            this._originalTableRows = res.categories.map((category) => ({
                categoryId: category.categoryId,
                label: category.label,
                slug: category.slug,
                labelError: "",
                slugError: "",
                count: category.count ?? 0,
            }));
        });
    }

    /**
     * 数字のみの文字列かどうかを判定
     * → true: DB に登録されたカテゴリの ID（"1", "42", …）
     */
    @action.bound
    public isPersistedId(id: string): boolean {
        // UUID には必ずハイフンが含まれるので、
        // ハイフンがない＝数字IDとみなす
        return !id.includes("-");
    }

    /**
     * 削除確認ダイアログを開き、
     * OK が押されたら removeCategory(id) を呼び出す
     */
    @action.bound
    public confrmDeleteCategory(id: string, label: string, count: number) {
        const isUsedDocument = count > 0;
        const message = buildDeleteMessage(label, isUsedDocument);
        deleteDialogStore.open("カテゴリ削除", message, () => this.removeCategory(id));
    }

    @action.bound
    public async updateCategoryGroup() {
        const dto = new CategoryGroupInput({
            slug: this.groupSlug,
            label: this.groupLabel,
            categories: this.tableRows.map(
                (r) =>
                    new CategoryInputOutput({
                        slug: r.slug,
                        label: r.label,
                        categoryId: this.isPersistedId(r.categoryId) ? r.categoryId : undefined,
                    }),
            ),
        });

        try {
            await this.adminApi.putCategoryGroup(
                {mediaId: this.mediaId},
                {categoryGroupId: this.categoryGroupId},
                dto,
            );
            this.pageRouter.pushRoute(
                `/admin/${this.mediaId}/category-groups/${this.categoryGroupId}`,
            );
        } catch (error) {
            if (error instanceof HttpError) {
                console.error(error.message);
                this.duplicateError = error.message;
            }
        }
    }

    @computed
    public get canUpdate() {
        if (!super.canSave()) return false;

        // (2) 編集モード：ロード直後のデータと同一なら保存不可
        if (this._originalCategoryGroup.label) {
            const groupUnchanged =
                this.groupLabel === this._originalCategoryGroup.label &&
                this.groupSlug === this._originalCategoryGroup.slug;

            const rowsUnchanged =
                this.tableRows.length === this._originalTableRows.length &&
                this.tableRows.every((r, i) => {
                    const o = this._originalTableRows[i];
                    return (
                        r.categoryId === o.categoryId &&
                        r.label === o.label &&
                        r.slug === o.slug &&
                        r.count === o.count
                    );
                });

            if (groupUnchanged && rowsUnchanged) return false;
        }

        return true;
    }
}
